import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import AvisoPrivacidadContent from './legal/AvisoPrivacidadContent';
import TerminosContent from './legal/TerminosContent';

/**
 * LegalModal — Modal con tabs para Aviso de privacidad / Términos.
 * Evita el problema de scroll al cambiar de ruta y preserva el contexto del usuario.
 *
 * Props:
 *   open: boolean
 *   onClose: () => void
 *   initialTab: 'aviso' | 'terminos'
 */
export default function LegalModal({ open, onClose, initialTab = 'aviso' }) {
  const [tab, setTab] = useState(initialTab);
  const bodyRef = useRef(null);

  // Sync initialTab when modal reopens with a different request
  useEffect(() => {
    if (open) setTab(initialTab);
  }, [open, initialTab]);

  // Lock body scroll while modal open
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  // Reset scroll inside modal when switching tab
  useEffect(() => {
    if (bodyRef.current) bodyRef.current.scrollTop = 0;
  }, [tab]);

  // Close on ESC
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  const isAviso = tab === 'aviso';

  return createPortal(
    <div
      className="legal-modal-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="legal-modal-title"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      data-testid="legal-modal"
    >
      <div className="legal-modal-panel">
        <header className="legal-modal-header">
          <div className="legal-modal-tabs" role="tablist">
            <button
              type="button"
              role="tab"
              aria-selected={isAviso}
              className={`legal-modal-tab${isAviso ? ' active' : ''}`}
              onClick={() => setTab('aviso')}
              data-testid="legal-modal-tab-aviso"
            >
              Aviso de privacidad
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={!isAviso}
              className={`legal-modal-tab${!isAviso ? ' active' : ''}`}
              onClick={() => setTab('terminos')}
              data-testid="legal-modal-tab-terminos"
            >
              Términos y condiciones
            </button>
          </div>
          <button
            type="button"
            className="legal-modal-close"
            onClick={onClose}
            aria-label="Cerrar"
            data-testid="legal-modal-close"
          >
            <span className="icon-font">close</span>
          </button>
        </header>

        <div className="legal-modal-body" ref={bodyRef}>
          <div className="legal-modal-meta">
            <span className="text-label legal-modal-eyebrow">documento legal</span>
            <h2 id="legal-modal-title" className="legal-modal-title">
              {isAviso ? 'Aviso de privacidad integral' : 'Términos y condiciones de uso'}
            </h2>
            <p className="legal-modal-last-update">Última actualización: 5 de junio de 2026</p>
          </div>
          <article className="legal-article legal-modal-article">
            {isAviso ? <AvisoPrivacidadContent /> : <TerminosContent />}
          </article>
        </div>
      </div>
    </div>,
    document.body
  );
}
